/**
 * Headshot Component
 */

import React, { FC, useState, useMemo } from 'react';
import { HeadshotProps } from './headshot.types';
import Image from '../Image/image';
import Skeleton from '../Skeleton/skeleton';
import COLORS from '../../style/universal.color';

const sizeRange = [30, 60, 90, 120, 150, 180, 240];

const HeadshotFallback: string = 'https://prod-gameday.mlbstatic.com/responsive-gameday-assets/1.2.0/images/players/default-batter.svg';

const generateUrl = (scaling: number, playerId: string | number, zoomLevel: number, width: number) => {
  const headshotWidth = width * scaling;
  const zoom = Math.round(headshotWidth / zoomLevel);

  return `https://img.mlbstatic.com/mlb-photos/image/upload/d_headshot_silo_generic.png,w_${headshotWidth},h_${headshotWidth}/c_crop,w_${zoom},g_face/v1/people/${playerId}/headshot/silo/current`;
}

// want to keep to a range of acceepted sizes to keep asset costs down
const generateRoundedSize = (width: number | string) => {
  const rangeLength = sizeRange.length;
  let headshotWidth = sizeRange[rangeLength - 1];
  const widthNum = typeof width === 'string' ? width.replace('px', '') : width;
  for (let i = 0; i < rangeLength; i++) {
    if (sizeRange[i] >= widthNum) {
      headshotWidth = sizeRange[i];
      break;
    }
  }

  return headshotWidth;
};

const constructHeadshotURLSrcset = (playerId: string | number, zoomLevel: number, width: string | number) => {
  // set img to appropriate width
  // TODO what if percentage?
  let headshotWidth = generateRoundedSize(width);

  return {
    '1x': generateUrl(1, playerId, zoomLevel, headshotWidth),
    '2x': generateUrl(2, playerId, zoomLevel, headshotWidth),
    '3x': generateUrl(3, playerId, zoomLevel, headshotWidth)
  }
};

const Headshot: FC<HeadshotProps> = (props: HeadshotProps) => {
  const {
    src = '',
    srcset,
    playerId,
    rounded = true,
    errorImageSrc = HeadshotFallback,
    backgroundColor = COLORS.mlbGrey1,
    isLoading,
    width = '60px',
    closeUp = true,
    zoomScale = 1.3,
    errorZoomScale = 1
  } = props;

  const [error, setError] = useState(false);
  const zoomLevel = closeUp
    ? error
      ? errorZoomScale
      : zoomScale
    : 1;

  const providedSrcset = playerId && useMemo(() => constructHeadshotURLSrcset(playerId, zoomLevel, width), [playerId, zoomLevel, width]) || srcset;
  const providedSrc: string = playerId && providedSrcset && providedSrcset['1x'] || src;

  const handlers = {
    onError: () => { setError(true) }
  }

  const HeadshotUI = isLoading
    ? <Skeleton width={ width } height={ width } isCircle={ rounded } />
    : (
        <Image
          { ...props }
          key={ playerId }
          src={ providedSrc }
          srcset={ providedSrcset }
          rounded={ rounded }
          errorImageSrc={ errorImageSrc }
          handlers={ handlers }
          backgroundColor={ backgroundColor }
          sizes={ typeof width === 'number' ? `${width}px` : width }
        />
    );

  return HeadshotUI;
};

export default Headshot;
